import { scaleLinear, scaleTime } from 'd3-scale';

import type { ChartV2Point, ChartV2Series } from '../types/chart-v2.types';
import { bisectLeft, isOhlcPoint, pointValue } from '../utils/helpers';

export interface Extent {
  min: number;
  max: number;
}

export interface Viewport {
  width: number;
  height: number;
  padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

export interface SeriesScale {
  seriesId: string;
  yScale: (value: number) => number;
  yDomain: [number, number];
  color: string;
}

export interface Scales {
  xScale: (value: Date | number) => number;
  yScale: (value: number) => number;
  xInvert: (pixel: number) => number;
  xDomain: [number, number];
  yDomain: [number, number];
  plot: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  perSeries: SeriesScale[];
  barTimeline?: number[];
}

const pointInDomain = (p: ChartV2Point, domain: [number, number]): boolean =>
  p.time >= domain[0] && p.time <= domain[1];

const extentForSeries = (
  s: ChartV2Series,
  xDomain: [number, number]
): Extent => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const p of s.data) {
    if (!pointInDomain(p, xDomain)) {
      continue;
    }

    if (isOhlcPoint(p)) {
      if (p.high > max) {
        max = p.high;
      }

      if (p.low < min) {
        min = p.low;
      }
    } else {
      const v = pointValue(p);

      if (v > max) {
        max = v;
      }

      if (v < min) {
        min = v;
      }
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { min: 0, max: 1 };
  }

  if (min === max) {
    return { min: min - 1, max: max + 1 };
  }

  const pad = (max - min) * 0.08;

  return { min: min - pad, max: max + pad };
};

const hasPointInDomain = (
  series: ChartV2Series,
  xDomain: [number, number]
): boolean => series.data.some(p => pointInDomain(p, xDomain));

export const computeYExtent = (
  series: ChartV2Series[],
  xDomain: [number, number]
): Extent => {
  // Series with no points in the visible window would otherwise pull
  // `extentForSeries` into its `{ 0, 1 }` fallback and compress the
  // merged y-domain down to the floor — visible at runtime as
  // "indexed-to-100" panes that look squashed when one of the series
  // has no data in the current zoom range. Filter those out.
  const extents = series
    .filter(s => hasPointInDomain(s, xDomain))
    .map(s => extentForSeries(s, xDomain));

  if (extents.length === 0) {
    return { min: 0, max: 1 };
  }

  return {
    min: Math.min(...extents.map(e => e.min)),
    max: Math.max(...extents.map(e => e.max)),
  };
};

export interface CreateScalesInput {
  viewport: Viewport;
  xDomain: [number, number];
  seriesWithColors: Array<{ series: ChartV2Series; color: string }>;
  /**
   * When true, every series shares a single y-domain computed from the
   * union of all series extents. Used by the `indexed` price-display mode
   * where all series render on a common `%` axis.
   */
  mergeYDomain?: boolean;
  barTimeline?: number[];
}

type XFns = {
  xScale: (value: Date | number) => number;
  xInvert: (pixel: number) => number;
};

const buildIndexXFns = (
  domainBars: number[],
  plotX: number,
  plotWidth: number
): XFns => {
  const n = domainBars.length;
  const slotWidth = plotWidth / n;

  return {
    xScale: (value: Date | number): number => {
      const ms = value instanceof Date ? value.getTime() : value;
      const idx = bisectLeft(domainBars, ms);

      if (idx < n && domainBars[idx] === ms) {
        return plotX + (idx + 0.5) * slotWidth;
      }

      return plotX + Math.min(idx, n) * slotWidth;
    },
    xInvert: (pixel: number): number => {
      const idx = Math.max(
        0,
        Math.min(n - 1, Math.floor((pixel - plotX) / slotWidth))
      );

      return domainBars[idx];
    },
  };
};

const buildTimeXFns = (
  xDomain: [number, number],
  plotX: number,
  plotWidth: number
): XFns => {
  const rawX = scaleTime()
    .domain([new Date(xDomain[0]), new Date(xDomain[1])])
    .range([plotX, plotX + plotWidth]);

  return {
    xScale: (value: Date | number): number => {
      const out = rawX(value instanceof Date ? value : new Date(value));

      return typeof out === 'number' ? out : Number.NaN;
    },
    xInvert: (pixel: number): number => rawX.invert(pixel).getTime(),
  };
};

export const createScales = (input: CreateScalesInput): Scales => {
  const { viewport, xDomain, seriesWithColors, mergeYDomain, barTimeline } =
    input;
  const { padding } = viewport;
  const plot = {
    x: padding.left,
    y: padding.top,
    width: Math.max(0, viewport.width - padding.left - padding.right),
    height: Math.max(0, viewport.height - padding.top - padding.bottom),
  };

  // Index-based scale: each bar gets an equal slot, eliminating trading-session
  // gaps (overnight, weekends). Mirrors V1's scaleLinear([0, lastIdx]) approach.
  const domainBars = barTimeline
    ? barTimeline.filter(t => t >= xDomain[0] && t <= xDomain[1])
    : null;

  const { xScale, xInvert } =
    domainBars && domainBars.length >= 2
      ? buildIndexXFns(domainBars, plot.x, plot.width)
      : buildTimeXFns(xDomain, plot.x, plot.width);

  const sharedExtent = mergeYDomain
    ? computeYExtent(
        seriesWithColors.map(s => s.series),
        xDomain
      )
    : null;

  const perSeries: SeriesScale[] = seriesWithColors.map(({ series, color }) => {
    const extent = sharedExtent ?? extentForSeries(series, xDomain);
    const raw = scaleLinear()
      .domain([extent.min, extent.max])
      .range([plot.y + plot.height, plot.y]);

    const yScale = (v: number): number => {
      const out = raw(v);

      return typeof out === 'number' ? out : Number.NaN;
    };

    return {
      seriesId: series.id,
      yScale,
      yDomain: [extent.min, extent.max],
      color,
    };
  });

  const primary = perSeries[0];
  const yScale = primary ? primary.yScale : (v: number) => v;
  const yDomain: [number, number] = primary ? primary.yDomain : [0, 1];

  return {
    xScale,
    yScale,
    xInvert,
    xDomain,
    yDomain,
    plot,
    perSeries,
    barTimeline: barTimeline ?? undefined,
  };
};
